/**
 * Client-side mirror of the API's `RecentSharePointSitesHelper.NormalizeWebUrl`
 * (api/MarkupLegal.Management.Core/Users/Preferences). Keep the two in step:
 * the API is the source of truth and rejects what this rejects.
 */
export type SharePointSiteUrlRejection =
  "invalid" | "notSharePoint" | "oneDrive";

export type SharePointSiteUrlResult =
  | {
      ok: true;
      webUrl: string;
      displayName: string;
      /** Document library named by the link, relative to the site (e.g. "Delte dokumenter"). */
      list?: string;
      /** Folder inside `list` named by the link (e.g. "Schultz dokumenter/Forretningsgange"). */
      folder?: string;
    }
  | { ok: false; reason: SharePointSiteUrlRejection };

const SHAREPOINT_HOST_SUFFIX = ".sharepoint.com";
const ONEDRIVE_HOST_SUFFIX = "-my.sharepoint.com";
const ADMIN_HOST_SUFFIX = "-admin.sharepoint.com";

/** Managed paths under which SharePoint Online creates site collections. */
const SITE_COLLECTION_PREFIXES = new Set(["sites", "teams", "portals"]);

/** Office sharing links: `/:f:/s/{site}/...`, `/:w:/t/{team}/...`, `/:b:/r/sites/{site}/...`. */
const SHARING_LINK_SCOPES: Record<string, string> = { s: "sites", t: "teams" };

const isSharingLinkMarker = (segment: string) =>
  segment.length === 3 && segment[0] === ":" && segment[2] === ":";

/** Libraries that hold pages and assets rather than documents; never worth opening the picker in. */
const SYSTEM_LIBRARIES = new Set([
  "sitepages",
  "pages",
  "siteassets",
  "style library",
  "_layouts",
  "_catalogs",
  "_api",
  "_vti_bin",
]);

type SiteMatch = {
  /** `sites/Legal`, or null for the tenant root site. */
  sitePath: string | null;
  /** Encoded path segments after the site, for list/folder resolution. */
  rest: string[];
};

const resolveSite = (segments: string[]): SiteMatch => {
  if (segments.length === 0) return { sitePath: null, rest: [] };

  if (isSharingLinkMarker(segments[0]) && segments.length >= 3) {
    // Sharing links carry an opaque item id, not a browsable path.
    const scope = SHARING_LINK_SCOPES[segments[1].toLowerCase()];
    if (scope) return { sitePath: `${scope}/${segments[2]}`, rest: [] };
    if (segments[1].toLowerCase() === "r")
      return { ...resolveSite(segments.slice(2)), rest: [] };
    return { sitePath: null, rest: [] };
  }

  const managedPath = segments[0].toLowerCase();
  if (segments.length < 2 || !SITE_COLLECTION_PREFIXES.has(managedPath))
    return { sitePath: null, rest: segments };

  return { sitePath: `${managedPath}/${segments[1]}`, rest: segments.slice(2) };
};

/**
 * The library and folder a browser link points at, if any. SharePoint's modern
 * library UI encodes the current folder as a server-relative path in `?id=`
 * (older links use `RootFolder`), which wins over the path in the URL itself.
 */
const resolveListAndFolder = (
  encodedRest: string[],
  params: URLSearchParams,
  sitePath: string | null
): { list?: string; folder?: string } => {
  let segments = encodedRest.map(safeDecode);

  const folderParam = params.get("id") ?? params.get("RootFolder");
  if (folderParam?.startsWith("/")) {
    const folderSegments = folderParam.split("/").filter(Boolean);
    // `sitePath` keeps the URL's percent-encoding; the query parameter is
    // already decoded, so compare decoded segments.
    const siteSegments = sitePath ? sitePath.split("/").map(safeDecode) : [];
    const belongsToSite = siteSegments.every(
      (segment, index) =>
        folderSegments[index]?.toLowerCase() === segment.toLowerCase()
    );
    if (belongsToSite) segments = folderSegments.slice(siteSegments.length);
  }

  // Everything from the library's "Forms" view folder onwards is UI, and a
  // trailing .aspx is a page, not a folder.
  const formsIndex = segments.findIndex((s) => s.toLowerCase() === "forms");
  if (formsIndex >= 0) segments = segments.slice(0, formsIndex);
  if (segments[segments.length - 1]?.toLowerCase().endsWith(".aspx"))
    segments = segments.slice(0, -1);

  if (segments.length === 0) return {};

  // Custom lists live under `Lists/<name>`; document libraries sit directly under the web.
  const listSegmentCount =
    segments[0].toLowerCase() === "lists" && segments.length >= 2 ? 2 : 1;
  const list = segments.slice(0, listSegmentCount).join("/");
  if (SYSTEM_LIBRARIES.has(list.toLowerCase())) return {};

  const folder = segments.slice(listSegmentCount).join("/");
  return folder ? { list, folder } : { list };
};

/** `decodeURIComponent` throws on malformed escapes such as `Jura%`; fall back to the raw segment. */
const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export const normalizeSharePointSiteUrl = (
  input: string
): SharePointSiteUrlResult => {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return { ok: false, reason: "invalid" };
  }
  if (url.protocol !== "https:") return { ok: false, reason: "invalid" };

  const host = url.hostname.toLowerCase();
  if (
    !host.endsWith(SHAREPOINT_HOST_SUFFIX) ||
    host.endsWith(ADMIN_HOST_SUFFIX)
  ) {
    return { ok: false, reason: "notSharePoint" };
  }
  if (host.endsWith(ONEDRIVE_HOST_SUFFIX))
    return { ok: false, reason: "oneDrive" };

  const origin = `https://${host}`;
  const { sitePath, rest } = resolveSite(
    url.pathname.split("/").filter(Boolean)
  );
  const target = resolveListAndFolder(rest, url.searchParams, sitePath);

  if (!sitePath)
    return { ok: true, webUrl: origin, displayName: host, ...target };

  return {
    ok: true,
    webUrl: `${origin}/${sitePath}`,
    displayName: safeDecode(sitePath.split("/")[1]),
    ...target,
  };
};
